<script setup lang="ts">
import {
  AdminEditorPreviewFrame,
  type AdminEditorPreviewViewport,
  UiSegmentedControl,
  UiStandardButton,
  UiText,
} from '@shopexperts/shared';
import { shallowRef } from 'vue';
import type { PageMetafieldsPreviewTarget, PageMetafieldsPreviewTargetOption } from '../../utils/metafieldsPreviewUrl';

const props = defineProps<{
  url: string;
  revision: number;
  error?: string;
  label: string;
  labelPath: string;
  isFullscreen: boolean;
  viewport: AdminEditorPreviewViewport;
  viewportWidth: number | null;
  viewportOptions: ReadonlyArray<{ label: string; value: AdminEditorPreviewViewport }>;
  previewTarget?: PageMetafieldsPreviewTarget;
  previewTargetOptions?: ReadonlyArray<PageMetafieldsPreviewTargetOption>;
}>();

const emit = defineEmits<{
  'update:viewport': [viewport: AdminEditorPreviewViewport];
  'update:previewTarget': [target: PageMetafieldsPreviewTarget];
  toggleFullscreen: [];
  frameReady: [iframe: HTMLIFrameElement];
  frameLoad: [];
}>();

const frameRef = shallowRef<HTMLIFrameElement | null>(null);

function handleFrameReady(iframe: HTMLIFrameElement | null) {
  frameRef.value = iframe;
  if (iframe) {
    emit('frameReady', iframe);
  }
}

defineExpose({
  getFrame: () => frameRef.value,
});
</script>

<template>
  <div class="flex min-h-0 flex-col overflow-hidden bg-se-gray-50 p-4">
    <div class="mb-3 flex items-center justify-between gap-3">
      <UiText as="h2" variant="h6" text="Preview" />
      <div class="flex items-center gap-2">
        <div
          v-if="props.previewTarget && props.previewTargetOptions && props.previewTargetOptions.length > 1"
          class="w-36"
          data-testid="preview-target-toggle"
        >
          <UiSegmentedControl
            :model-value="props.previewTarget"
            :options="props.previewTargetOptions"
            @update:model-value="(value) => { if (value) emit('update:previewTarget', value) }"
          />
        </div>
        <UiStandardButton label="Open in new tab" icon="link-external" variant="ghost" size="sm" :href="props.url" />
      </div>
    </div>
    <AdminEditorPreviewFrame
      :url="props.url"
      :revision="props.revision"
      :error="props.error"
      :label="props.label"
      :label-path="props.labelPath"
      :is-fullscreen="props.isFullscreen"
      :viewport="props.viewport"
      :viewport-options="props.viewportOptions"
      :viewport-width="props.viewportWidth"
      @update:viewport="emit('update:viewport', $event)"
      @toggle-fullscreen="emit('toggleFullscreen')"
      @frame-ready="handleFrameReady"
      @frame-load="emit('frameLoad')"
    />
  </div>
</template>
